import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { AlertTriangle, RotateCw } from 'lucide-react'
import useAppStore from '../../../store/useAppStore'
import Loader from '../../../components/Loader'
import useAudioManager from '../../../hooks/useAudioManager'
import { hapticImpact } from '../../../utils/haptic'
import eventApi from '../services/eventApi'
import HalloweenHero from '../components/halloween/HalloweenHero'
import WitchCard from '../components/halloween/WitchCard'
import PlayerCard from '../components/halloween/PlayerCard'
import TeamProgressCard from '../components/halloween/TeamProgressCard'
import LadderCard from '../components/halloween/LadderCard'
import PrizesCard from '../components/halloween/PrizesCard'
import TopTeamsCard from '../components/halloween/TopTeamsCard'
import DrawBoard from '../components/halloween/DrawBoard'
import FairnessCard from '../components/halloween/FairnessCard'
import RulesCard from '../components/halloween/RulesCard'
import { SECTIONS_VARIANT, SECTION_VARIANT } from '../components/halloween/shared'

// Тыквы пересчитываются раз в минуту — чаще раза в 60 сек страницу не опрашиваем
const POLL_SECONDS = 60
// В drawing без известного времени следующего места («ведьма тянет билеты»)
const DRAW_WAIT_POLL_SECONDS = 20

// Через сколько секунд перезапросить страницу: по таймеру фазы или открытию следующего места
const getNextPollSeconds = (data) => {
  if (data.phase === 'drawing') {
    const nextReveal = data.draw?.next_reveal_in
    return nextReveal != null ? Math.max(2, nextReveal + 1) : DRAW_WAIT_POLL_SECONDS
  }
  return Math.min(POLL_SECONDS, Math.max(2, (Number(data.phase_ends_in) || 0) + 2))
}

/**
 * Страница эвента «Проклятие Skinoz» (GET /api/event/halloween/).
 * Фазы строго по phase: active | soon_draw | drawing | results.
 * { enabled: false } — эвента нет: уводим на главную, заглушек не рисуем.
 * Гостю приходит всё, кроме личного (you / my_team = null).
 */
const HalloweenEventPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isGuest = useAppStore((state) => state.isGuest)
  const openLogin = useAppStore((state) => state.openLogin)
  const { playSound } = useAudioManager({ select: 'select' })

  const [data, setData] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(false)
  const mountedRef = useRef(true)

  const loadEvent = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setIsLoading(true)
      setError(false)
    }
    try {
      const response = await eventApi.getHalloweenEvent()
      if (!mountedRef.current) return
      setData(response)
      setError(false)
    } catch (err) {
      if (!mountedRef.current || err?.name === 'AbortError') return
      console.error('❌ Хэллоуин: ошибка загрузки эвента:', err)
      if (!silent) setError(true)
    } finally {
      if (mountedRef.current && !silent) setIsLoading(false)
    }
  }, [])

  // Первичная загрузка + мгновенный рефетч при возврате во вкладку
  useEffect(() => {
    mountedRef.current = true
    loadEvent()
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') loadEvent({ silent: true })
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => {
      mountedRef.current = false
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [loadEvent])

  // Следующий опрос планируется после каждого ответа: по таймеру фазы или следующему месту
  useEffect(() => {
    if (!data || data.enabled === false) return undefined
    const timeout = setTimeout(() => {
      if (document.visibilityState === 'visible') loadEvent({ silent: true })
    }, getNextPollSeconds(data) * 1000)
    return () => clearTimeout(timeout)
  }, [data, loadEvent])

  // Эвента нет (ещё не начался или результаты уже скрыты) — уводим на главную
  useEffect(() => {
    if (data?.enabled === false) navigate('/', { replace: true })
  }, [data, navigate])

  const openTeam = (id) => {
    playSound('select')
    hapticImpact('light')
    navigate(`/event/team/${encodeURIComponent(id)}`)
  }

  if (isLoading && !data) return <Loader message="" />

  if (error && !data) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-6 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-red-500/10">
          <AlertTriangle size={28} className="text-red-400" />
        </div>
        <p className="text-sm text-text-secondary">{t('halloweenEvent.error')}</p>
        <button
          type="button"
          onClick={() => loadEvent()}
          className="hev-cta flex items-center gap-2 rounded-xl px-6 py-2.5 text-sm font-bold transition-transform active:scale-[0.98]"
        >
          <RotateCw size={16} />
          {t('halloweenEvent.retry')}
        </button>
      </div>
    )
  }

  if (!data || data.enabled === false) return null

  const { phase, rules, prizes, ladder = [], witch, you, my_team: myTeam, top = [], draw } = data
  const isCollecting = phase === 'active' || phase === 'soon_draw'
  const showDraw = phase === 'drawing' || phase === 'results'

  return (
    <div className="px-4 pb-10 pt-4 text-white lg:px-0 lg:pt-6">
      <motion.div
        variants={SECTIONS_VARIANT}
        initial="hidden"
        animate="visible"
        className="mx-auto w-full lg:max-w-6xl"
      >
        <div className="space-y-4 lg:grid lg:grid-cols-12 lg:items-start lg:gap-6 lg:space-y-0">
          {/* Левая колонка: герой, ведьма, игрок */}
          <div className="space-y-4 lg:col-span-5">
            <motion.div variants={SECTION_VARIANT}>
              <HalloweenHero
                phase={phase}
                endsIn={data.ends_in}
                drawIn={data.draw_in}
                phaseEndsIn={data.phase_ends_in}
              />
            </motion.div>

            {isCollecting && (
              <motion.div variants={SECTION_VARIANT}>
                <PlayerCard
                  phase={phase}
                  you={you}
                  myTeam={myTeam}
                  rules={rules}
                  isGuest={isGuest}
                  onLogin={openLogin}
                />
              </motion.div>
            )}

            <motion.div variants={SECTION_VARIANT}>
              <WitchCard witch={witch} witchPrize={prizes?.witch} />
            </motion.div>
          </div>

          {/* Правая колонка: розыгрыш, банда, лестница, призы, топ, правила */}
          <div className="space-y-4 lg:col-span-7">
            {showDraw && (
              <motion.div variants={SECTION_VARIANT}>
                <DrawBoard
                  phase={phase}
                  draw={draw}
                  prizes={prizes}
                  phaseEndsIn={data.phase_ends_in}
                  hasTeam={Boolean(myTeam)}
                  onOpenTeam={openTeam}
                />
              </motion.div>
            )}

            {myTeam && (
              <motion.div variants={SECTION_VARIANT}>
                <TeamProgressCard
                  team={myTeam}
                  title={t('halloweenEvent.team.title')}
                  onOpen={() => openTeam(myTeam.id)}
                  actionLabel={t('halloweenEvent.team.openPlayers')}
                />
              </motion.div>
            )}

            {isCollecting && (
              <motion.div variants={SECTION_VARIANT}>
                <PrizesCard prizes={prizes} tickets={myTeam ? myTeam.pumpkins : null} />
              </motion.div>
            )}

            {isCollecting && (
              <motion.div variants={SECTION_VARIANT}>
                <LadderCard ladder={ladder} nextStep={myTeam?.next_step} minContributors={rules?.min_contributors} />
              </motion.div>
            )}

            <motion.div variants={SECTION_VARIANT}>
              <TopTeamsCard top={top} myTeam={myTeam} onOpen={openTeam} />
            </motion.div>

            <motion.div variants={SECTION_VARIANT}>
              <FairnessCard draw={draw} />
            </motion.div>

            <motion.div variants={SECTION_VARIANT}>
              <RulesCard rules={rules} prizes={prizes} ladder={ladder} />
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default HalloweenEventPage
